import React, { useState } from "react";
import styles from "./input-meter-number.module.scss";
import { BeneficiaryItem } from "../../../../service/beneficiary-service";
import { MeterValidationResponse } from "../../buy-electricity/buy-electricity.types";
import { BeneficiarySelectModal } from "../../buy-electricity/component/beneficiary-select-modal";
import { ChevronRight } from "../../../../assets/icons";
import { useFeatureFlag } from "../../../../tenant/features/useFeatureFlag";
import { FeatureFlagName } from "../../../../tenant/features/featureFlags";

export interface InputMeterNumberProps {
	value: string;
	onChange: (value: string) => void;
	paymentType: "prepaid" | "postpaid" | null;
	onBeneficiarySelect: (beneficiary: BeneficiaryItem) => void;
	isValidating?: boolean;
	validationData?: MeterValidationResponse | null;
	validationError?: string;
	currencySymbol?: string;
	outstandingAmount?: number;
	hideSavedBeneficiaries?: boolean;
}

export const InputMeterNumber: React.FC<InputMeterNumberProps> = ({
	value,
	onChange,
	paymentType,
	onBeneficiarySelect,
	isValidating = false,
	validationData = null,
	validationError,
	currencySymbol = "",
	outstandingAmount,
	hideSavedBeneficiaries = false,
}) => {
	const [showModal, setShowModal] = useState(false);
	const savedBeneficiariesOn = useFeatureFlag(FeatureFlagName.SAVED_BENEFICIARIES);

	const handleBeneficiarySelect = (b: BeneficiaryItem) => {
		onBeneficiarySelect(b);
		setShowModal(false);
	};

	const showOutstandingBanner = outstandingAmount !== undefined && outstandingAmount > 0;

	return (
		<>
			<header className={styles.cardHeader}>
				<span className={styles.cardLabel}>Meter Number</span>
				{!hideSavedBeneficiaries && savedBeneficiariesOn && (
					<button
						type="button"
						className={styles.savedBeneficiariesLink}
						onClick={() => setShowModal(true)}
					>
						Saved Beneficiaries
						<ChevronRight />
					</button>
				)}
			</header>

			<div className={styles.cardBody}>
				<input
					type="text"
					inputMode="numeric"
					value={value}
					onChange={e => onChange(e.target.value)}
					placeholder="Enter meter no. (e.g., 57689464638)"
					maxLength={18}
					className={styles.meterInputField}
				/>

				{isValidating && (
					<div className={styles.meterStatus}>
						<span className={styles.meterStatusSpinner} />
						Validating meter…
					</div>
				)}

				{!isValidating && validationData && !validationError && (
					<div className={`${styles.meterStatus} ${styles.meterStatusOk}`}>
						<span className={styles.meterStatusIcon}>✓</span>
						<span className={styles.meterStatusName}>{validationData.customerName}</span>
						{validationData.outstandingBalance !== undefined && (
							<span className={styles.meterStatusBalance}>
								Outstanding: {currencySymbol}
								{validationData.outstandingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
							</span>
						)}
					</div>
				)}

				{!isValidating && validationError && (
					<div className={`${styles.meterStatus} ${styles.meterStatusError}`}>
						{validationError}
					</div>
				)}
			</div>

			{showOutstandingBanner && (
				<div className={styles.outstandingBanner}>
					<span className={styles.outstandingLabel}>Your outstanding balance is:</span>
					<span className={styles.outstandingAmount}>
						{currencySymbol}
						{(outstandingAmount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
					</span>
				</div>
			)}

			{showModal && (
				<BeneficiarySelectModal
					paymentType={paymentType}
					onSelect={handleBeneficiarySelect}
					onClose={() => setShowModal(false)}
				/>
			)}
		</>
	);
};
